import React, { useEffect, useState } from "react";
import {
  OrderPrice,
  PriceOverrideBreakdownItem,
  PricingAdjustmentCreate,
  PricingAdjustmentType,
  PricingBreakDownItem,
  PricingList,
  Shipment,
  SurchargePricingRule,
  useModifyOrderPricingMutation,
} from "@api/graphql/generated/generated-types";
import { Button, Loading, Label } from "@src/common/components";
import { ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import DollarInput from "../../../Input/DollarInput";
import { useCourierLocale } from "@src/common/courierLocale";
import { useEiaFuelRateByRuleId } from "@src/common/components/EiaFuelRateNote";
import SurchagePricingRulesForm, {
  EditableSurchargePricingRule,
} from "@src/orders/create/components/SurchagePricingRulesForm";
import { useDebounce } from "usehooks-ts";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useCourieStore } from "@src/common/lib/store";
import PricingBreakDownItemEdit from "./PricingBreakDownItemEdit";
import PricingBreakDownItemComponent from "./PricingBreakDownItemComponent";
import OrderPriceFormOverride from "./OrderPriceFormOverride";
import { useUpdateTotalPrice } from "@src/common/hooks/useUpdateTotalPrice";
import { useActionBy } from "@src/common/hooks/useActionBy";
import { PricingFinalizationControls } from "./PricingFinalizationControls";
import { useAuth } from "@src/common/hooks/useAuth";
import {
  doesPriceBreakdownMatchExpectedTotal,
  isQuantityAlwaysSpecifiedInPriceBreakdown,
  normalizePriceBreakdownItems,
} from "@src/common/utils/priceBreakdown";

export const OrdersPriceForm = ({
  selectedShipment,
  onModifyOrderPricing,
  orderPrice: orderPriceProp,
}: {
  selectedShipment: Shipment | null | undefined;
  onModifyOrderPricing?: () => void;
  orderPrice?: OrderPrice | undefined;
}) => {
  const { showToast } = useCourieStore();
  const { currencyDisplay } = useCourierLocale();
  const { updatedBy } = useActionBy();
  const { courierId, courieUser, employeeId } = useAuth();
  const [
    modifyOrderPricingMutation,
    {
      loading: modifyOrderPricingMutationLoading,
      error: modifyOrderPricingMutationError,
    },
  ] = useModifyOrderPricingMutation();
  const [isOverrideInputDirty, setIsOverrideInputDirty] = useState(false);
  const [removedItems, setRemovedItems] = useState<Record<string, boolean>>({});
  const [orderPrice, setOrderPrice] =
    useState<OrderPrice | undefined>(undefined);
  const removedItemsDebounced = useDebounce<any>(removedItems, 500);
  const [totalPriceOverideValue, setTotalPriceOverideValue] =
    useState<undefined | number>(0);
  const [surchargePricings, setSurchargePricings] = useState<
    EditableSurchargePricingRule[]
  >([]);
  const [basePriceOverrideMode, setBasePriceOverrideMode] = useState(false);
  const [basePriceOverrideValue, setBasePriceOverrideValue] = useState(0);
  const [editItems, setEditItems] = useState<
    Record<number, PricingBreakDownItem>
  >({});
  const [breakdownItems, setBreakdownItems] = useState<
    PriceOverrideBreakdownItem[]
  >([]);
  const orderId = selectedShipment?.order.id;
  // Dedicated query, skipped when no order is selected; shared by all breakdown items.
  const eiaFuelRateByRuleId = useEiaFuelRateByRuleId(orderId);
  const selectedPricingListId = selectedShipment?.order.endCustomer.pricingList;

  const { updateTotalPrice, previewOrderPricingModificationMutationLoading } =
    useUpdateTotalPrice({
      removedItems: removedItemsDebounced,
      surchargePricings,
      orderId: selectedShipment?.order.id,
      orderPrice,
      setTotalPriceOverideValue,
    });

  useEffect(() => {
    if (orderPriceProp) {
      setIsOverrideInputDirty(orderPriceProp.isPriceOverride);
      if (orderPriceProp.basePriceOverride) {
        setBasePriceOverrideValue(orderPriceProp.basePriceOverride);
      } else {
        setBasePriceOverrideValue(0);
      }

      setBreakdownItems(
        normalizePriceBreakdownItems(orderPriceProp.priceOverrideBreakdown)
      );
    }
    if (orderPriceProp) {
      setOrderPrice(orderPriceProp);
      setTotalPriceOverideValue(Number(orderPriceProp.total));
      setRemovedItems({});
      setEditItems({});
    }
  }, [orderPriceProp]);

  useEffect(() => {
    if (modifyOrderPricingMutationError) {
      showErrorToast(modifyOrderPricingMutationError, showToast);
    }
  }, [modifyOrderPricingMutationError]);

  useEffect(() => {
    if (surchargePricings?.length === 0) {
      return;
    }
    updateTotalPrice();
  }, [selectedShipment, surchargePricings]);

  useEffect(() => {
    if (Object.keys(removedItemsDebounced).length === 0) {
      return;
    }
    updateTotalPrice();
  }, [selectedShipment, removedItemsDebounced]);

  const onSave = () => {
    const adjustmentIdsToRemove = Object.entries(removedItemsDebounced)
      .filter(([_, isRemoved]) => isRemoved)
      .map(([adjustmentId]) => adjustmentId);

    if (orderId != undefined && totalPriceOverideValue != undefined) {
      let requestedPricingAdjustments: PricingAdjustmentCreate[] = [];
      surchargePricings.forEach((row: EditableSurchargePricingRule) => {
        const isCustom = row.id ? false : true;
        if (isCustom) {
          requestedPricingAdjustments.push({
            amount_total: Number(row.amount),
            type: PricingAdjustmentType.Custom,
            note: row.note || undefined,
            amountDriverPay: row.amountDriverPay !== undefined ? Number(row.amountDriverPay) : undefined,
          });
        } else {
          requestedPricingAdjustments.push({
            type: PricingAdjustmentType.RuleBased,
            surchargePricingRuleId: row.id,
          });
        }
      });

      const editItemCount = Object.entries(editItems).length;

      if (isOverrideInputDirty && breakdownItems.length > 0) {
        if (!isQuantityAlwaysSpecifiedInPriceBreakdown(breakdownItems)) {
          showToast({
            message: "All quantities must be provided",
            type: "error",
          });
          return;
        }

        if (
          !doesPriceBreakdownMatchExpectedTotal(
            breakdownItems,
            totalPriceOverideValue
          )
        ) {
          showToast({
            message: "Breakdown items do not match total price override",
            type: "error",
          });
          return;
        }
      }

      if (!courierId) return;

      modifyOrderPricingMutation({
        variables: {
          courierId,
          orderId,
          adjustmentsToAdd: isOverrideInputDirty
            ? []
            : requestedPricingAdjustments,
          adjustmentIdsToRemove: isOverrideInputDirty
            ? []
            : adjustmentIdsToRemove,
          priceOverride: isOverrideInputDirty
            ? totalPriceOverideValue
            : undefined,
          priceOverrideBreakdown:
            breakdownItems.length > 0
              ? breakdownItems.map(({ description, amount, quantity }) => ({
                  description,
                  amount,
                  quantity: quantity || 1,
                }))
              : undefined,
          basePriceOverride:
            basePriceOverrideMode || basePriceOverrideValue
              ? basePriceOverrideValue
              : null,
          adjustmentOverrides:
            editItemCount > 0
              ? Object.entries(editItems).map(([adjustmentId, { amount }]) => ({
                  adjustmentId: adjustmentId,
                  amount: amount || 0,
                }))
              : undefined,
          updatedBy: updatedBy,
        },
      })
        .then((result) => {
          onModifyOrderPricing && onModifyOrderPricing();
          setSurchargePricings([]);
          setBasePriceOverrideMode(false);
        })
        .catch((e) => {
          showErrorToast(e, showToast);
        });
    }
  };

  const handleUndoOverride = () => {
    if (
      orderId &&
      totalPriceOverideValue !== undefined &&
      totalPriceOverideValue !== null &&
      courierId
    ) {
      const variables = {
        courierId,
        orderId,
        adjustmentsToAdd: undefined,
        adjustmentIdsToRemove: undefined,
        priceOverride: undefined,
        basePriceOverrideValue: basePriceOverrideValue,
        updatedBy: employeeId ? employeeId : courieUser!.id,
      };
      modifyOrderPricingMutation({
        variables,
      }).then((result) => {
        onModifyOrderPricing && onModifyOrderPricing();
        setIsOverrideInputDirty(false);
      });
    }
  };

  const undoBasePriceOverride = () => {
    if (orderId && courierId) {
      modifyOrderPricingMutation({
        variables: {
          courierId,
          orderId,
          basePriceOverride: null,
          updatedBy: employeeId ? employeeId : courieUser!.id,
        },
      }).then((result) => {
        onModifyOrderPricing && onModifyOrderPricing();
        setIsOverrideInputDirty(false);
      });
    }
  };

  if (orderPrice) {
    return (
      <div className="order-price-form container mx-auto rounded-md px-2">
        {!isOverrideInputDirty ? (
          <>
            {orderPrice &&
              orderPrice.pricingBreakDown.items.map((item, i) => {
                const adjustmentId = item.adjustmentId || "";
                return (
                  <React.Fragment key={i}>
                    {editItems[adjustmentId] ? (
                      <PricingBreakDownItemEdit
                        setEditItems={setEditItems}
                        pricingBreakDownItem={item}
                      />
                    ) : (
                      <PricingBreakDownItemComponent
                        basePriceOverrideMode={basePriceOverrideMode}
                        setBasePriceOverrideMode={setBasePriceOverrideMode}
                        PricingBreakDownItem={item}
                        removedItems={removedItems}
                        basePriceOverrideValue={basePriceOverrideValue}
                        setBasePriceOverrideValue={setBasePriceOverrideValue}
                        index={i}
                        setRemovedItems={setRemovedItems}
                        undoBasePriceOverride={undoBasePriceOverride}
                        eiaFuelRateByRuleId={eiaFuelRateByRuleId}
                        orderPrice={orderPrice}
                        setEditItems={setEditItems}
                        onModifyOrderPricing={onModifyOrderPricing}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            {!modifyOrderPricingMutationLoading && (
              <SurchagePricingRulesForm
                surchargePricings={surchargePricings}
                setSurchargePricings={setSurchargePricings}
                setValue={() => {}}
                isCompactList={true}
                showAddButton={!orderPrice.isPriceOverride}
                pricingList={selectedPricingListId as PricingList | undefined}
              />
            )}
          </>
        ) : (
          <OrderPriceFormOverride
            showUndoButton={!orderPrice.isPriceOverride}
            resetPriceOverride={() => {
              setTotalPriceOverideValue(Number(orderPrice.total));
              setIsOverrideInputDirty(false);
              setRemovedItems({});
              setSurchargePricings([]);
              setBreakdownItems([]);
            }}
            breakdownItems={breakdownItems}
            setBreakdownItems={setBreakdownItems}
            allowAddingItems={
              (isOverrideInputDirty &&
                totalPriceOverideValue &&
                totalPriceOverideValue != 0) ||
              false
            }
          />
        )}
        <div>
          <Label className="font-bold">Total</Label>
          <Loading loading={previewOrderPricingModificationMutationLoading}>
            <DollarInput
              dollar={Number(totalPriceOverideValue) || 0}
              setDollar={(value) => setTotalPriceOverideValue(value)}
              onChange={(event) => {
                const value = event.target.value;
                setTotalPriceOverideValue(Number(value));
                if (orderPrice.total !== Number(value)) {
                  setIsOverrideInputDirty(true);
                }
              }}
            />
          </Loading>
        </div>
        {orderPrice.taxBreakDown?.taxItems && orderPrice.taxBreakDown.taxItems.length > 0 && (
          <div className="mt-2 text-sm text-slate-400">
            {orderPrice.total != null && (
              <div className="flex justify-between py-1">
                <span>Total Before Tax</span>
                <span>{currencyDisplay}{orderPrice.total}</span>
              </div>
            )}
            {orderPrice.taxBreakDown.taxItems.map((item, index) => (
              <div key={`tax-${index}`} className="flex justify-between py-1">
                <span>{item.name}</span>
                <span>{currencyDisplay}{item.amount}</span>
              </div>
            ))}
            <div className="flex justify-between py-1 text-slate-500">
              <span>Total After Tax</span>
              <span>{currencyDisplay}{orderPrice.totalAfterTax}</span>
            </div>
          </div>
        )}
        <div className="flex justify-between mt-4 ">
          <div className="flex gap-2">
            {orderPrice.isPriceOverride ? (
              <span
                onClick={() => handleUndoOverride()}
                className="flex truncate items-center text-xs cursor-pointer text-blue-500 hover:underline"
              >
                <ArrowUturnLeftIcon className="h-4 w-4 mr-1" />
                Undo Override
              </span>
            ) : (
              <div></div>
            )}
            <PricingFinalizationControls
              isFinalized={orderPrice.isFinalized || false}
              orderId={orderId || ""}
              onFinalizationChange={onModifyOrderPricing}
            />
          </div>
          <Button
            size="xs"
            onClick={() => onSave()}
            isProcessing={modifyOrderPricingMutationLoading}
          >
            Save Changes
          </Button>
        </div>
      </div>
    );
  }
  return null;
};
